/**
 * useFirmLanguages — the languages THIS firm serves, with their names.
 * @file src/web/shared/hooks/useFirmLanguages.ts
 *
 * Every staff screen that offers a language — the form builder, the new-lead
 * dialog, the follow-up editor, settings — used to carry its own copy of the
 * list. They now ask this hook, which reads the firm's profile once and
 * joins it to the shared registry for names and direction.
 *
 * Until the profile has loaded, the hook returns the default set (the launch
 * three). That is a deliberate choice over an empty list: a builder that
 * renders with no languages for 200 ms flashes an empty checkbox row and,
 * worse, a component that initialises state from that empty list keeps it.
 * The default set is what a firm has until it chooses otherwise, so the
 * flash is at most "one option too many" for a moment, never a wrong one —
 * and never thirty tiles that then collapse to three.
 *
 * @llm-rule WHEN: A staff screen needs to offer or name a language
 * @llm-rule AVOID: A local LANGUAGES array — see src/shared/languages.ts
 */
import { useEffect, useState } from 'react';
import { api } from '@/shared/api';
import {
  BASE_LANGUAGE,
  DEFAULT_LANGUAGES,
  REGISTRY,
  type LanguageInfo,
} from '@shared/languages';
import type { PracticeArea } from '@shared/practice-areas';

interface FirmSettings {
  displayName?: string | null;
  languages?: string[];
  defaultLanguage?: string;
  timezone?: string;
  dateFormat?: string;
  /** The firm's own practice areas. Empty means they have not chosen. */
  practiceAreas?: string[];
}

/** What GET /api/firm-settings answers with, as the screens use it. */
export interface FirmProfilePayload {
  settings: FirmSettings;
  /**
   * Every matter type to OFFER: the firm's practice areas, plus any value
   * already on a form or a prospect. Served with the settings because every
   * screen that needs one needs the other.
   */
  practiceAreas: PracticeArea[];
  /** The firm's logo, as an API path, or null. */
  logoUrl: string | null;
  /** What the firm calls itself, for the workspace header. */
  firmName: string | null;
}

/*
 * One request per page load, shared by every caller on the page. The
 * profile changes when an admin saves settings, which is rare; a screen open
 * across that save will see the new list on its next mount.
 */
let cached: Promise<FirmProfilePayload> | null = null;

function fetchProfile(): Promise<FirmProfilePayload> {
  if (!cached) {
    cached = api
      .get<{
        settings: FirmSettings;
        practiceAreas?: PracticeArea[];
        logoUrl?: string | null;
        firm?: { name: string } | null;
      }>('/api/firm-settings')
      .then((r) => ({
        settings: r.settings ?? {},
        practiceAreas: r.practiceAreas ?? [],
        logoUrl: r.logoUrl ?? null,
        firmName: r.settings?.displayName?.trim() || r.firm?.name || null,
      }))
      .catch(() => {
        cached = null; // let the next caller try again
        return { settings: {}, practiceAreas: [], logoUrl: null, firmName: null };
      });
  }
  return cached;
}

function fetchSettings(): Promise<FirmSettings> {
  return fetchProfile().then((p) => p.settings);
}

export { fetchSettings as fetchFirmSettings, fetchProfile as fetchFirmProfile };

/** Call after saving settings so the next reader sees the new list. */
export function invalidateFirmLanguages() {
  cached = null;
}

/** The same cache, under the name a practice-area caller would look for. */
export { invalidateFirmLanguages as invalidateFirmProfile };

export interface FirmLanguages {
  /** Registry entries for the languages the firm serves, base first. */
  languages: LanguageInfo[];
  /** Codes only — for `includes` checks and select values. */
  codes: string[];
  defaultLanguage: string;
  loading: boolean;
}

function toInfo(codes: string[]): LanguageInfo[] {
  const seen = new Set<string>();
  const out: LanguageInfo[] = [];
  for (const code of [BASE_LANGUAGE, ...codes]) {
    if (seen.has(code)) continue;
    const info = REGISTRY.find((l) => l.code === code);
    if (info) {
      seen.add(code);
      out.push(info);
    }
  }
  return out;
}

export function useFirmLanguages(): FirmLanguages {
  const [state, setState] = useState<FirmLanguages>(() => ({
    languages: toInfo([...DEFAULT_LANGUAGES]),
    codes: [...DEFAULT_LANGUAGES],
    defaultLanguage: BASE_LANGUAGE,
    loading: true,
  }));

  useEffect(() => {
    let live = true;
    void fetchSettings().then((s) => {
      if (!live) return;
      const codes = s.languages?.length ? s.languages : [...DEFAULT_LANGUAGES];
      const languages = toInfo(codes);
      setState({
        languages,
        codes: languages.map((l) => l.code),
        defaultLanguage: s.defaultLanguage ?? BASE_LANGUAGE,
        loading: false,
      });
    });
    return () => {
      live = false;
    };
  }, []);

  return state;
}
